import {
  ILinkChildrenEventItem,
  ISecurityEventInfo,
} from '@app/core/interfaces/security-events.interface';
import { createFeature, createReducer, on } from '@ngrx/store';
import { ISelectItem } from 'ngx-mt-ui-lib/interfaces';
import { SecurityEventsActions } from './security-events.actions';
import { compareObjects } from 'ngx-mt-ui-lib/functions';
import { IEventTag } from '@app/core/interfaces/event.interface';
import { EStateStatus } from '@app/app.state';

export interface ISecurityEventsState {
  id: number | null;
  item: ISecurityEventInfo;
  form: Partial<ISecurityEventInfo>;
  tags: IEventTag[];
  hasChanges: boolean;
  protocolDirty: boolean;
  protocolSaving: boolean;
  loading: boolean;
  stateListWithCount: ISelectItem<{ count: number }, string>[];
  childEventList: ILinkChildrenEventItem[];
}

export const securityEventsInitialState: ISecurityEventsState = {
  id: null,
  item: null,
  form: null,
  tags: [],
  hasChanges: false,
  protocolDirty: false,
  protocolSaving: false,
  loading: false,
  stateListWithCount: [],
  childEventList: [],
};

export const SecurityEventsReducer = createReducer(
  securityEventsInitialState,
  on(SecurityEventsActions.selectItem, (state, payload) => ({
    ...securityEventsInitialState,
    id: payload.id,
  })),
  on(
    SecurityEventsActions.getStateListWithCount,
    SecurityEventsActions.requestCreateItem,
    SecurityEventsActions.requestUpdateItem,
    SecurityEventsActions.requestSetToWorking,
    SecurityEventsActions.requestSetToCompleted,
    SecurityEventsActions.requestSetToClose,
    SecurityEventsActions.requestSetToCancel,
    SecurityEventsActions.requestSetToArchive,
    SecurityEventsActions.requestAddParentSecurityEvent,
    SecurityEventsActions.requestAddChildSecurityEvent,
    SecurityEventsActions.requestAddChildDisruption,
    SecurityEventsActions.requestDeleteChildEvent,
    SecurityEventsActions.requestTags,
    SecurityEventsActions.requestEditTags,
    SecurityEventsActions.requestDeleteSystemTag,
    SecurityEventsActions.requestChildEventList,
    (state) => ({
      ...state,
      loading: true,
    }),
  ),
  on(SecurityEventsActions.getStateListWithCountSuccess, (state, payload) => ({
    ...state,
    stateListWithCount: payload.list,
    loading: false,
  })),
  on(SecurityEventsActions.discardOwnerDraft, (state, { id }) =>
    state.id === id ? { ...state, form: null, hasChanges: false } : state,
  ),
  on(SecurityEventsActions.protocolEditState, (state, { id, dirty, saving }) =>
    state.id === id
      ? { ...state, protocolDirty: dirty, protocolSaving: saving }
      : state,
  ),
  on(SecurityEventsActions.requestItemInfoSuccess, (state, payload) => ({
    ...state,
    id: payload.item.id,
    item: payload.item,
    hasChanges:
      payload.item.responseProcessMode === 'PROTOCOL' &&
      state.id === payload.item.id
        ? state.hasChanges
        : false,
    loading: false,
  })),
  on(SecurityEventsActions.requestCreateItemSuccess, (state, payload) => ({
    ...state,
    id: payload.item.id,
    item: payload.item,
    hasChanges: false,
    loading: false,
  })),
  on(SecurityEventsActions.requestUpdateItemSuccess, (state, payload) => ({
    ...state,
    id: payload.item.id,
    item: payload.item,
    hasChanges: false,
    loading: false,
  })),
  on(SecurityEventsActions.requestTagsSuccess, (state, { tags }) => ({
    ...state,
    tags,
    loading: false,
  })),
  on(SecurityEventsActions.requestChildEventListSuccess, (state, payload) => ({
    ...state,
    childEventList: payload?.childEventList ?? [],
  })),
  on(
    SecurityEventsActions.requestSetToWorkingSuccess,
    SecurityEventsActions.requestSetToCompletedSuccess,
    SecurityEventsActions.requestSetToCloseSuccess,
    SecurityEventsActions.requestSetToCancelSuccess,
    SecurityEventsActions.requestSetToArchiveSuccess,
    SecurityEventsActions.requestAddParentSecurityEventSuccess,
    SecurityEventsActions.requestAddChildSecurityEventSuccess,
    SecurityEventsActions.requestAddChildDisruptionSuccess,
    SecurityEventsActions.requestDeleteChildEventSuccess,
    SecurityEventsActions.requestEditTagsSuccess,
    SecurityEventsActions.requestDeleteSystemTagSuccess,
    SecurityEventsActions.requestChildEventListSuccess,
    (state) => ({
      ...state,
      hasChanges:
        state.item?.responseProcessMode === 'PROTOCOL'
          ? state.hasChanges
          : false,
      loading: false,
    }),
  ),
  on(
    SecurityEventsActions.getStateListWithCountError,
    SecurityEventsActions.requestItemInfoError,
    SecurityEventsActions.requestCreateItemError,
    SecurityEventsActions.requestUpdateItemError,
    SecurityEventsActions.requestSetToWorkingError,
    SecurityEventsActions.requestSetToCompletedError,
    SecurityEventsActions.requestSetToCloseError,
    SecurityEventsActions.requestSetToCancelError,
    SecurityEventsActions.requestSetToArchiveError,
    SecurityEventsActions.requestAddParentSecurityEventError,
    SecurityEventsActions.requestAddChildSecurityEventError,
    SecurityEventsActions.requestAddChildDisruptionError,
    SecurityEventsActions.requestDeleteChildEventError,
    SecurityEventsActions.requestTagsError,
    SecurityEventsActions.requestEditTagsError,
    SecurityEventsActions.requestChildEventListError,
    (state) => ({
      ...state,
      loading: false,
    }),
  ),

  on(
    SecurityEventsActions.requestEditTagsError,
    SecurityEventsActions.requestDeleteSystemTagError,
    (state, payload) => ({
      ...state,
      tags: [...state.tags],
      error: payload.error,
      status: EStateStatus.ERROR,
    }),
  ),

  on(SecurityEventsActions.setFormValues, (state, payload) => {
    const someField = Object.values(payload.form).some((v) => !!v);
    const hasChanges: boolean =
      someField && !compareObjects(payload.form, state.item, false);

    return {
      ...state,
      form: payload.form,
      hasChanges,
    };
  }),
);

export const SecurityEventsFeature = createFeature({
  name: 'SecurityEvents',
  reducer: SecurityEventsReducer,
});
